import React, { useState, useEffect } from 'react';
import { useCapabilities } from '../contexts/CapabilitiesContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Build as BuildIcon,
  Settings as SettingsIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { format, parseISO } from 'date-fns';

interface MaintenanceEvent {
  maintenance_id: string;
  timestamp: string;
  action_type: 'baseline_reset' | 'maintenance' | 'calibration';
  baseline_before: number;
  baseline_after: number;
  reset_method?: 'recent_proving' | 'manual_input' | 'selected_proving';
  manual_value?: number;
  reference_report_id?: string;
  // The selected proving's date — a selected-proving reset takes effect at that
  // proving, not when it was entered; shown as the event's effective date.
  reference_date?: string;
  performed_by: string;
  reason: string;
  supporting_docs: string[];
  notes?: string;
}

interface MaintenanceHistoryProps {
  meterProductId: string;
  meterId: string;
  productType: string;
  onClose?: () => void;
  embedded?: boolean; // If true, shows as embedded view without dialog wrapper
  onChanged?: () => void; // called after an event is deleted so the parent can reload the meter
}

export const MaintenanceHistory: React.FC<MaintenanceHistoryProps> = ({
  meterProductId,
  meterId,
  productType,
  onClose,
  embedded = false,
  onChanged,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const { can } = useCapabilities();   // deleting a maintenance event needs run_maintenance
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState<MaintenanceEvent | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<MaintenanceEvent[]>([]);
  const [filteredHistory, setFilteredHistory] = useState<MaintenanceEvent[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<MaintenanceEvent | null>(null);
  const [downloadingDoc, setDownloadingDoc] = useState<string | null>(null);
  const [viewingDoc, setViewingDoc] = useState<string | null>(null);
  const [docViewerOpen, setDocViewerOpen] = useState(false);
  const [docUrl, setDocUrl] = useState<string | null>(null);

  useEffect(() => {
    fetchMaintenanceHistory();
  }, [meterProductId]);

  useEffect(() => {
    // Filter history based on search term
    if (searchTerm) {
      const filtered = history.filter(event =>
        event.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.performed_by.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.action_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (event.notes && event.notes.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredHistory(filtered);
    } else {
      setFilteredHistory(history);
    }
  }, [searchTerm, history]);

  // Maintenance is append-only, so a mistyped baseline reset used to be "correctable" only by adding another
  // one — leaving both in the audit trail. Admins/Supervisors can now remove a single event; the backend
  // restores the baseline from the newest remaining event (or clears it) and re-analyses the meter-product,
  // exactly as it does when one is added.
  const deleteEvent = async (event: MaintenanceEvent) => {
    setDeleting(true);
    setError(null);
    try {
      const token = (await getIdTokenClaims())?.__raw;
      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/meters/${encodeURIComponent(meterProductId)}/reset-baseline`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'delete_maintenance', maintenance_id: event.maintenance_id }),
        }
      );
      if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Failed to delete the maintenance event');
      setPendingDelete(null);
      await fetchMaintenanceHistory();
      onChanged?.();          // let the parent reload the meter (health/limits changed)
    } catch (e: any) {
      setError(e?.message || 'Failed to delete the maintenance event');
    } finally {
      setDeleting(false);
    }
  };

  const fetchMaintenanceHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = (await getIdTokenClaims())?.__raw;
      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/meters/${encodeURIComponent(meterProductId)}/maintenance-history`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch maintenance history');
      }

      const data = await response.json();
      setHistory(data.history || []);
      setFilteredHistory(data.history || []);
    } catch (err) {
      console.error('Error fetching maintenance history:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = async (docKey: string) => {
    setDownloadingDoc(docKey);
    try {
      const token = (await getIdTokenClaims())?.__raw;
      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/meters/${encodeURIComponent(meterProductId)}/maintenance-docs?doc_id=${encodeURIComponent(docKey)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get document URL');
      }

      const data = await response.json();
      // Open the presigned URL in a new tab
      window.open(data.download_url, '_blank');
    } catch (err) {
      console.error('Error downloading document:', err);
      setError('Failed to download document');
    } finally {
      setDownloadingDoc(null);
    }
  };

  const handleViewDocument = async (docKey: string) => {
    setViewingDoc(docKey);
    try {
      const token = (await getIdTokenClaims())?.__raw;
      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/meters/${encodeURIComponent(meterProductId)}/maintenance-docs?doc_id=${encodeURIComponent(docKey)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get document URL');
      }

      const data = await response.json();
      setDocUrl(data.download_url);
      setDocViewerOpen(true);
    } catch (err) {
      console.error('Error viewing document:', err);
      setError('Failed to view document');
    } finally {
      setViewingDoc(null);
    }
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'baseline_reset':
        return <SettingsIcon />;
      case 'maintenance':
        return <BuildIcon />;
      case 'calibration':
        return <EditIcon />;
      default:
        return <SettingsIcon />;
    }
  };

  const getActionColor = (actionType: string): 'primary' | 'secondary' | 'warning' | 'error' | 'info' | 'success' => {
    switch (actionType) {
      case 'baseline_reset':
        return 'primary';
      case 'maintenance':
        return 'warning';
      case 'calibration':
        return 'info';
      default:
        return 'primary';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return format(parseISO(timestamp), 'MMM dd, yyyy HH:mm');
    } catch {
      return timestamp;
    }
  };

  const formatDate = (timestamp: string) => {
    try {
      return format(parseISO(timestamp), 'MMM dd, yyyy');
    } catch {
      return timestamp;
    }
  };

  const formatTime = (timestamp: string) => {
    try {
      return format(parseISO(timestamp), 'HH:mm:ss');
    } catch {
      return '';
    }
  };

  const calculateChange = (before: number, after: number) => {
    const change = after - before;
    const percentChange = ((change / before) * 100).toFixed(2);
    return {
      absolute: change.toFixed(4),
      percent: percentChange,
      isIncrease: change > 0,
    };
  };

  const renderHistoryContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Button size="small" onClick={fetchMaintenanceHistory} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      );
    }

    if (filteredHistory.length === 0) {
      return (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">
            {searchTerm ? 'No maintenance events found matching your search' : 'No maintenance history available'}
          </Typography>
        </Box>
      );
    }

    return (
      <Timeline position="alternate">
        {filteredHistory.map((event, index) => {
          const change = calculateChange(event.baseline_before, event.baseline_after);

          return (
            <TimelineItem key={event.maintenance_id}>
              <TimelineOppositeContent color="text.secondary">
                <Typography variant="body2">{formatDate(event.reference_date || event.timestamp)}</Typography>
                <Typography variant="caption">{formatTime(event.reference_date || event.timestamp)}</Typography>
                {event.reference_date && (
                  <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', opacity: 0.7 }}>
                    recorded {formatDate(event.timestamp)}
                  </Typography>
                )}
              </TimelineOppositeContent>

              <TimelineSeparator>
                <TimelineDot color={getActionColor(event.action_type)}>
                  {getActionIcon(event.action_type)}
                </TimelineDot>
                {index < filteredHistory.length - 1 && <TimelineConnector />}
              </TimelineSeparator>

              <TimelineContent>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 3 }
                  }}
                  onClick={() => setSelectedEvent(event)}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box>
                        <Typography variant="subtitle2" component="div">
                          {event.action_type.replace('_', ' ').toUpperCase()}
                        </Typography>
                        {event.reset_method && (
                          <Chip
                            label={event.reset_method === 'manual_input' ? 'Manual' : 'Recent Proving'}
                            size="small"
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                      <Box textAlign="right">
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {change.isIncrease ? (
                            <TrendingUpIcon color="error" fontSize="small" />
                          ) : (
                            <TrendingDownIcon color="success" fontSize="small" />
                          )}
                          <Typography variant="body2" fontWeight="bold">
                            {change.percent}%
                          </Typography>
                          {can('run_maintenance') && (
                            <Tooltip title="Delete this maintenance event — the meter's baseline and health are recalculated from the remaining history">
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); setPendingDelete(event); }}
                                sx={{ ml: 0.5 }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    <Box mb={1}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {event.baseline_before.toFixed(4)} → {event.baseline_after.toFixed(4)}
                      </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {event.reason}
                    </Typography>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {event.performed_by || 'Unknown'}
                        </Typography>
                      </Box>

                      {event.supporting_docs.length > 0 && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <AttachFileIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {event.supporting_docs.length} file(s)
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    );
  };

  const confirmDeleteDialog = (
    <Dialog
      open={!!pendingDelete}
      onClose={() => !deleting && setPendingDelete(null)}
      maxWidth="xs"
      fullWidth
      // Sits on top of the whole nested stack: maintenance modal (1400) -> history (1500) ->
      // event details (2000) -> doc viewer (2100). MUI portals every Dialog at 1300 by default,
      // so without this the confirmation renders BEHIND the history dialog that opened it.
      sx={{ zIndex: 2200 }}
    >
      <DialogTitle>Delete this maintenance event?</DialogTitle>
      <DialogContent>
        {pendingDelete && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{pendingDelete.action_type.replace('_', ' ')}</strong> on {new Date(pendingDelete.timestamp).toLocaleString()} by {pendingDelete.performed_by || 'unknown'}
              {' '}— baseline {pendingDelete.baseline_before.toFixed(4)} → {pendingDelete.baseline_after.toFixed(4)}.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {meterId} · {productType} will be re-analysed immediately: its baseline reverts to the most recent remaining
              maintenance event (or to the meter's own history if this is the last one), and health, control limits and
              financial impact are recalculated. This cannot be undone.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPendingDelete(null)} disabled={deleting}>Cancel</Button>
        <Button color="error" variant="contained" disabled={deleting}
          startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
          onClick={() => pendingDelete && deleteEvent(pendingDelete)}>
          {deleting ? 'Deleting…' : 'Delete & re-analyse'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const content = (
    <Box>
      {confirmDeleteDialog}
      <Box mb={3}>
        <TextField
          fullWidth
          placeholder="Search maintenance history..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          {filteredHistory.length} event(s) found
        </Typography>
        <IconButton onClick={fetchMaintenanceHistory} size="small">
          <RefreshIcon />
        </IconButton>
      </Box>

      {renderHistoryContent()}

      {/* Event Details Dialog */}
      <Dialog
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 2000 }} // Ensure it's above all other modals
      >
        {selectedEvent && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1}>
                  {getActionIcon(selectedEvent.action_type)}
                  <Typography variant="h6">Maintenance Details</Typography>
                </Box>
                <IconButton onClick={() => setSelectedEvent(null)} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent dividers>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CalendarTodayIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={selectedEvent.reference_date ? 'Effective date (selected proving)' : 'Date & Time'}
                    secondary={
                      selectedEvent.reference_date
                        ? `${formatTimestamp(selectedEvent.reference_date)} · recorded ${formatTimestamp(selectedEvent.timestamp)}`
                        : formatTimestamp(selectedEvent.timestamp)
                    }
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Performed By"
                    secondary={selectedEvent.performed_by || 'Unknown user'}
                  />
                </ListItem>

                <Divider component="li" />

                <ListItem>
                  <ListItemText
                    primary="Baseline Change"
                    secondary={
                      <Box>
                        <Typography variant="body2">
                          Before: {selectedEvent.baseline_before.toFixed(4)}
                        </Typography>
                        <Typography variant="body2" color="primary">
                          After: {selectedEvent.baseline_after.toFixed(4)}
                        </Typography>
                        {(() => {
                          const change = calculateChange(selectedEvent.baseline_before, selectedEvent.baseline_after);
                          return (
                            <Typography variant="body2" color={change.isIncrease ? 'error' : 'success'}>
                              Change: {change.absolute} ({change.percent}%)
                            </Typography>
                          );
                        })()}
                      </Box>
                    }
                  />
                </ListItem>

                {selectedEvent.reset_method && (
                  <ListItem>
                    <ListItemText
                      primary="Reset Method"
                      secondary={selectedEvent.reset_method === 'manual_input' ?
                        `Manual Input (${selectedEvent.manual_value?.toFixed(4)})` :
                        'Recent Proving Report'
                      }
                    />
                  </ListItem>
                )}

                {selectedEvent.reference_report_id && (
                  <ListItem>
                    <ListItemText
                      primary="Reference Report"
                      secondary={selectedEvent.reference_report_id}
                    />
                  </ListItem>
                )}

                <Divider component="li" />

                <ListItem>
                  <ListItemText
                    primary="Reason"
                    secondary={selectedEvent.reason}
                  />
                </ListItem>

                {selectedEvent.notes && (
                  <ListItem>
                    <ListItemText
                      primary="Additional Notes"
                      secondary={selectedEvent.notes}
                    />
                  </ListItem>
                )}

                {selectedEvent.supporting_docs.length > 0 && (
                  <>
                    <Divider component="li" />
                    <ListItem>
                      <ListItemText primary="Supporting Documents" />
                    </ListItem>
                    {selectedEvent.supporting_docs.map((doc, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <DescriptionIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={doc}
                          secondary="View or download document"
                        />
                        <ListItemSecondaryAction>
                          <Box display="flex" gap={1}>
                            <IconButton
                              onClick={() => handleViewDocument(doc)}
                              disabled={viewingDoc === doc}
                              title="View document"
                            >
                              {viewingDoc === doc ? (
                                <CircularProgress size={20} />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={() => handleDownloadDocument(doc)}
                              disabled={downloadingDoc === doc}
                              title="Download document"
                            >
                              {downloadingDoc === doc ? (
                                <CircularProgress size={20} />
                              ) : (
                                <DownloadIcon />
                              )}
                            </IconButton>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </>
                )}
              </List>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setSelectedEvent(null)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog
        open={docViewerOpen}
        onClose={() => {
          setDocViewerOpen(false);
          setDocUrl(null);
        }}
        maxWidth="lg"
        fullWidth
        sx={{ zIndex: 2100 }} // Higher than event details (2000)
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Document Viewer</Typography>
            <IconButton
              onClick={() => {
                setDocViewerOpen(false);
                setDocUrl(null);
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {docUrl ? (
            <Box sx={{ height: '70vh', width: '100%' }}>
              {docUrl.toLowerCase().includes('.pdf') ? (
                <iframe
                  src={docUrl}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="Document Viewer"
                />
              ) : (
                <img
                  src={docUrl}
                  alt="Supporting Document"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              )}
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => docUrl && window.open(docUrl, '_blank')} disabled={!docUrl}>
            Open in New Tab
          </Button>
          <Button onClick={() => {
            setDocViewerOpen(false);
            setDocUrl(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (embedded) {
    return content;
  }

  // Return as a dialog if not embedded
  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 1500 }} // Higher than maintenance modal (1301) and confirmation (1302)
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <SettingsIcon color="primary" />
            <Typography variant="h6">
              Maintenance History - {meterId} ({productType})
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {content}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};